import { Navigate, useParams } from "react-router-dom";
import { localizePath } from "../utils/localePaths";
import Home from "../pages/Home/Home";
import Terms from "../pages/Legal/Terms";
import Privacy from "../pages/Legal/Privacy";
import ExtensionPrivacy from "../pages/Legal/ExtensionPrivacy";
import Features from "../pages/Features/Features";
import MultiCollection from "../pages/Features/features/MultiCollection";
import Encryption from "../pages/Features/features/Encryption";
import Mcp from "../pages/Features/features/Mcp";
import AITagging from "../pages/Features/features/AITagging";
import Spaces from "../pages/Features/features/Spaces";
import GlobalSearch from "../pages/Features/features/GlobalSearch";
import Import from "../pages/Features/features/Import";
import Archiving from "../pages/Features/features/Archiving";
import Cleanup from "../pages/Features/features/Cleanup";
import Rediscover from "../pages/Features/features/Rediscover";
import Offline from "../pages/Features/features/Offline";
import DuplicateDetection from "../pages/Features/features/DuplicateDetection";
import Security from "../pages/Security/Security";
import Pricing from "../pages/Pricing/Pricing";
import BlogPage from "../pages/Blog/BlogPage";
import BlogPostPage from "../pages/Blog/BlogPostPage";
import PocketAlternative from "../pages/Misc/PocketAlternative";
import CompetitorAlternative from "../pages/Alternatives/CompetitorAlternative";
import InstapaperAlternative from "../pages/Alternatives/InstapaperAlternative";
import OmnivoreAlternative from "../pages/Alternatives/OmnivoreAlternative";
import UseCasePage from "../pages/UseCases/UseCasePage";
import BookmarksForSchools from "../pages/UseCases/BookmarksForSchools";
import BookmarksForTeachers from "../pages/UseCases/BookmarksForTeachers";
import About from "../pages/About/About";
import Extensions from "../pages/Download/Extensions";
import DocsLandingPage from "../pages/Docs/DocsLandingPage";
import DocsPage from "../pages/Docs/DocsPage";
import NotFound from "../pages/Misc/NotFound";
import DirectoryPage from "../pages/Directory/DirectoryPage";
import CollectionDetailPage from "../pages/Directory/CollectionDetailPage";

// Raindrop still uses the shared living-competitor template. Instapaper and
// Omnivore have bespoke pages (imported above).
const RaindropAlternative = () => <CompetitorAlternative competitor="raindrop" />;

// Exact-match keyword URL for the schools page. Renders the same component but
// English-only (no hreflang cluster) with the canonical pinned to the primary
// /bookmarks-for-schools so the two URLs consolidate instead of competing.
const SchoolsExactMatch = () => (
  <BookmarksForSchools
    localized={false}
    canonicalOverride="https://onbookmarks.com/bookmarks-for-schools/"
  />
);

// Use case wrapper components
const DevelopersPage = () => <UseCasePage useCase="developers" />;
const ResearchersPage = () => <UseCasePage useCase="researchers" />;
const StudentsPage = () => <UseCasePage useCase="students" />;

// Redirect components
const RedirectToExtensions = () => {
  const { lang } = useParams();
  return <Navigate to={localizePath("/download/extensions", lang)} replace />;
};

export const publicRoutes = [
  {
    path: "/",
    Component: Home,
  },
  {
    path: "/features",
    Component: Features,
  },
  {
    path: "/features/multi-collection",
    Component: MultiCollection,
  },
  {
    path: "/features/encryption",
    Component: Encryption,
  },
  {
    path: "/features/mcp",
    Component: Mcp,
  },
  {
    path: "/features/ai-tagging",
    Component: AITagging,
  },
  {
    path: "/features/spaces",
    Component: Spaces,
  },
  {
    path: "/features/global-search",
    Component: GlobalSearch,
  },
  {
    path: "/features/import",
    Component: Import,
  },
  {
    path: "/features/archiving",
    Component: Archiving,
  },
  {
    path: "/features/cleanup",
    Component: Cleanup,
  },
  {
    path: "/features/rediscover",
    Component: Rediscover,
  },
  {
    path: "/features/offline",
    Component: Offline,
  },
  {
    path: "/features/duplicate-detection",
    Component: DuplicateDetection,
  },
  {
    path: "/security",
    Component: Security,
  },
  {
    path: "/blog",
    Component: BlogPage,
  },
  {
    path: "/blog/:slug",
    Component: BlogPostPage,
  },
  {
    path: "/legal/terms-of-service",
    Component: Terms,
  },
  {
    path: "/legal/privacy-policy",
    Component: Privacy,
  },
  {
    path: "/legal/extension-privacy-policy",
    Component: ExtensionPrivacy,
  },
  {
    path: "/pricing",
    Component: Pricing,
  },
  {
    path: "/pocket-alternative",
    Component: PocketAlternative,
  },
  {
    path: "/migrate-from-pocket",
    Component: PocketAlternative,
  },
  {
    path: "/pocket-shutdown",
    Component: PocketAlternative,
  },
  {
    path: "/pocket-replacement",
    Component: PocketAlternative,
  },
  {
    path: "/raindrop-alternative",
    Component: RaindropAlternative,
  },
  {
    path: "/instapaper-alternative",
    Component: InstapaperAlternative,
  },
  {
    path: "/omnivore-alternative",
    Component: OmnivoreAlternative,
  },
  {
    path: "/bookmarks-for-developers",
    Component: DevelopersPage,
  },
  {
    path: "/bookmarks-for-researchers",
    Component: ResearchersPage,
  },
  {
    path: "/bookmarks-for-students",
    Component: StudentsPage,
  },
  {
    path: "/bookmarks-for-schools",
    Component: BookmarksForSchools,
  },
  {
    // Exact-match keyword URL; renders the schools page English-only, which
    // canonicals back to /bookmarks-for-schools so the two don't compete.
    path: "/bookmark-organizer-for-schools",
    Component: SchoolsExactMatch,
  },
  {
    path: "/bookmarks-for-teachers",
    Component: BookmarksForTeachers,
  },
  {
    path: "/about",
    Component: About,
  },
  {
    path: "/features/browser-extension",
    Component: RedirectToExtensions,
  },
  {
    path: "/download/extensions",
    Component: Extensions,
  },
  {
    path: "/docs",
    Component: DocsLandingPage,
  },
  {
    path: "/docs/*",
    Component: DocsPage,
  },
  {
    path: "/directory",
    Component: DirectoryPage,
  },
  {
    path: "/directory/:uuid/:domain",
    Component: CollectionDetailPage,
  },
  {
    path: "*",
    Component: NotFound,
  },
];
